'use client';

import { useEffect, useState } from 'react';
import { getCookie, setCookie } from 'cookies-next';
import { usePathname, useRouter } from 'next/navigation';

import {
  MobileCollectionUpdateModal,
  DesktopCollectionUpdateModal,
} from './CollectionUpdateModal.parts';

import {
  useAuth,
  useDeviceCheck,
  useLocalStorageState,
  useInitialModalStatus,
} from '@/shared/hooks';
import { PATH, STORAGE_KEY } from '@/shared/constants';
import { ModalStatus, UIOverlayModalProps } from '@/shared/types';

const COLLECTION_UPDATE_MODAL_STATE_KEY = 'collection_update_modal_state';

const CollectionUpdateModal = ({
  rootId,
}: Omit<UIOverlayModalProps, 'isOpen'>) => {
  const router = useRouter();
  const pathName = usePathname();

  const { isAuth } = useAuth();
  const { isDesktop } = useDeviceCheck();
  const { status } = useInitialModalStatus();

  const [isReadyToShowModal, setIsReadyToShowModal] = useState(false);
  const [tutorialCompleted] = useLocalStorageState(STORAGE_KEY.TUTORIAL, false);

  const pathWithoutTrailingSlash = pathName.replace(/\/$/, '');
  const isLandingPage =
    pathWithoutTrailingSlash === PATH.HOME || pathWithoutTrailingSlash === '';
  const isContentsDetailPage = pathName.includes(PATH.DETAIL);
  const isCollectionPage = pathName.includes('collection');
  const isOpen = !isCollectionPage && isReadyToShowModal;
  const isOnBoardingFinished = status.onboarding === ModalStatus.COMPLETED;
  const isMarketingFinished = status.marketing === ModalStatus.COMPLETED;

  // 컨텐츠 상세 페이지가 아닌곳에서 페이지 이동이 발생할때 모달 노출 프로세스
  useEffect(() => {
    if (isContentsDetailPage) return;

    const collectionUpdateModalState = getCookie(
      COLLECTION_UPDATE_MODAL_STATE_KEY
    );

    // 이미 노출한 경우
    if (collectionUpdateModalState) return;

    // 아직 노출하지 않은 비회원 유저인 경우
    if (!isAuth) {
      return setIsReadyToShowModal(true);
    }

    // 회원이고, 온보딩이 완료된 경우
    if (isOnBoardingFinished) {
      return setIsReadyToShowModal(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName]);

  // 로그인한 유저가, 컨텐츠 상세가 아닌 페이지에서, 페이지 이동 없이 이전 모달 프로세스가 완료된 경우의 모달 노출 프로세스
  useEffect(() => {
    const collectionUpdateModalState = getCookie(
      COLLECTION_UPDATE_MODAL_STATE_KEY
    );

    if (collectionUpdateModalState || isContentsDetailPage) return;

    if (!isOnBoardingFinished) return;

    if (!isLandingPage) {
      return setIsReadyToShowModal(true);
    }

    if (isMarketingFinished) {
      return setIsReadyToShowModal(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnBoardingFinished, isMarketingFinished]);

  // 컨텐츠 상세페이지에서 모달 노출 프로세스
  useEffect(() => {
    if (isContentsDetailPage) {
      const collectionUpdateModalState = getCookie(
        COLLECTION_UPDATE_MODAL_STATE_KEY
      );

      // 이미 노출한경우
      if (collectionUpdateModalState) return;

      // 비회원 유저이고, 튜토리얼을 완료한 경우
      if (!isAuth && tutorialCompleted) {
        return setIsReadyToShowModal(true);
      }

      // 회원이고, 온보딩이 완료된 경우
      if (tutorialCompleted && isOnBoardingFinished) {
        return setIsReadyToShowModal(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName, tutorialCompleted, isOnBoardingFinished]);

  const setCollectionUpdateCookie = (state: 'seen' | 'closed') => {
    const expires =
      state === 'closed'
        ? new Date(new Date().setDate(new Date().getDate() + 3))
        : new Date(new Date().setFullYear(new Date().getFullYear() + 1));

    setCookie(COLLECTION_UPDATE_MODAL_STATE_KEY, state, {
      expires,
    });

    setIsReadyToShowModal(false);
  };

  const handleClose = () => {
    setCollectionUpdateCookie('closed');
  };

  const handleSubmit = () => {
    setCollectionUpdateCookie('seen');
    router.push(PATH.COLLECTION);
  };

  if (isDesktop) {
    return (
      <DesktopCollectionUpdateModal
        rootId={rootId}
        isOpen={isOpen}
        handleClose={handleClose}
        handleSubmit={handleSubmit}
      />
    );
  }

  return (
    <MobileCollectionUpdateModal
      isOpen={isOpen}
      handleClose={handleClose}
      handleSubmit={handleSubmit}
    />
  );
};

export default CollectionUpdateModal;
